import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { Printer, FileArrowDown, Check, Info } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Etichette() {
  const [products, setProducts] = useState([]);
  const [formats, setFormats] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [copies, setCopies] = useState(1);
  const [format, setFormat] = useState("avery_l7160_21");
  const [startOffset, setStartOffset] = useState(0);
  const [drawBorder, setDrawBorder] = useState(true);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const [p, f] = await Promise.all([api.get("/products"), api.get("/labels/formats")]);
    setProducts(p.data);
    setFormats(f.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map((p) => p.id)));
  };

  const currentFormat = formats.find((f) => f.key === format);
  const totalLabels = selected.size * Number(copies || 1);
  const pages = currentFormat
    ? Math.ceil((totalLabels + Number(startOffset || 0)) / currentFormat.per_page)
    : 1;

  const generatePDF = async () => {
    if (selected.size === 0) return toast.error("Seleziona almeno un prodotto");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/labels/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_ids: Array.from(selected),
          copies_per_product: Number(copies),
          format,
          start_offset: Number(startOffset),
          draw_border: drawBorder,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Errore generazione PDF");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etichette_${format}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF generato · ${totalLabels} etichette · ${pages} pagine`);
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="etichette-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Stampa Etichette EAN-13 · Formati Standard
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Etichette
          </h1>
        </div>
        <button
          onClick={generatePDF}
          disabled={loading || selected.size === 0}
          data-testid="generate-pdf-btn"
          className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors disabled:opacity-60 self-end"
        >
          <FileArrowDown size={14} weight="bold" />
          {loading ? "Generazione…" : `Scarica PDF (${totalLabels})`}
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] p-5 mb-5 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
            Formato foglio etichette
          </label>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger data-testid="label-format-select" className="rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {formats.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentFormat && (
            <div className="mt-2 text-[11px] text-[#4B5563] font-mono-data">
              {currentFormat.per_page} etichette/pagina · cella {currentFormat.cell_w_mm}×
              {currentFormat.cell_h_mm} mm · foglio {currentFormat.page_w_mm}×
              {currentFormat.page_h_mm} mm
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
            Copie per prodotto
          </label>
          <input
            type="number"
            min="1"
            data-testid="copies-input"
            className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
            value={copies}
            onChange={(e) => setCopies(e.target.value)}
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
            Salta N celle iniziali
          </label>
          <input
            type="number"
            min="0"
            data-testid="start-offset-input"
            className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
            value={startOffset}
            onChange={(e) => setStartOffset(e.target.value)}
            title="Utile per riutilizzare fogli parzialmente già usati"
          />
        </div>

        <div className="lg:col-span-4 flex items-center gap-4 pt-2 border-t border-[#E5E7EB]">
          <label className="flex items-center gap-2 text-xs text-[#4B5563]">
            <input
              type="checkbox"
              data-testid="draw-border-checkbox"
              checked={drawBorder}
              onChange={(e) => setDrawBorder(e.target.checked)}
              className="w-4 h-4 accent-[#1D3557]"
            />
            Disegna bordo grigio attorno a ogni etichetta (non consigliato su rotoli termici)
          </label>
          <div className="ml-auto text-xs text-[#4B5563] flex items-center gap-2">
            <Info size={14} weight="bold" color="#1D3557" />
            <span>
              <span className="font-mono-data font-semibold text-[#0A0A0A]">{totalLabels}</span>{" "}
              etichette ·{" "}
              <span className="font-mono-data font-semibold text-[#0A0A0A]">{pages}</span> pagine
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB]">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
          <button
            onClick={toggleAll}
            data-testid="toggle-all-btn"
            className="text-xs font-display font-semibold uppercase tracking-wider text-[#1D3557] hover:text-[#2A4B7C]"
          >
            {selected.size === products.length ? "Deseleziona tutto" : "Seleziona tutto"}
          </button>
          <div className="font-mono-data text-xs text-[#4B5563]">
            {selected.size} / {products.length} selezionati
          </div>
        </div>
        {products.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#4B5563]">
            Nessun prodotto disponibile.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
            {products.map((p) => {
              const isSel = selected.has(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  data-testid={`label-card-${p.id}`}
                  className={`text-left border p-3 transition-colors ${
                    isSel
                      ? "border-[#1D3557] bg-[#F3F4F6]"
                      : "border-[#E5E7EB] bg-white hover:border-[#1D3557]/50"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A0A0A] truncate">
                        {p.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                        {p.type.replace("_", " ")}
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
                        isSel ? "bg-[#1D3557] border-[#1D3557]" : "border-[#E5E7EB]"
                      }`}
                    >
                      {isSel && <Check size={12} weight="bold" color="white" />}
                    </div>
                  </div>
                  <img
                    src={`${API_BASE}/labels/barcode/${p.barcode}.png`}
                    alt={p.barcode}
                    className="w-full h-20 object-contain bg-white"
                  />
                  <div className="mt-2 font-mono-data text-xs text-center text-[#4B5563]">
                    {p.barcode}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-[#E5E7EB] p-4 flex items-start gap-3 text-xs text-[#4B5563]">
        <Printer size={16} weight="bold" color="#1D3557" className="flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-semibold uppercase tracking-wider text-[10px] text-[#0A0A0A] mb-1">
            Suggerimenti Stampa
          </div>
          <ul className="space-y-1 list-disc ml-4">
            <li>
              Per <b>fogli adesivi A4 Avery</b>: scegli il codice sulla confezione (L7160, L7163…) e stampa <b>senza scalatura</b> (100%) dal dialog di stampa.
            </li>
            <li>
              Per <b>stampanti termiche</b> (Zebra, TSC, Brother QL…): scegli un formato "Rotolo termico" e imposta la dimensione carta corrispondente nel driver della stampante.
            </li>
            <li>
              <b>Salta celle iniziali</b>: se un foglio è già stato parzialmente usato, inserisci il numero di etichette già utilizzate per iniziare dalla posizione giusta.
            </li>
            <li>
              Disattiva il bordo grigio prima di stampare su carta adesiva pre-fustellata.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
